import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  CreatedTable,
  ExtractedTableCreationService,
} from '../../../service/extracted-data/extracted-table-creation.service';
import { TableComponent } from '../../ui/table/table.component';
import { ToolbarService } from '../../../service/app-state/toolbar.service';
import { ExtractedTableRemovalService } from '../../../service/extracted-data/extracted-table-removal.service';
import { PageNavigationService } from '../../../service/app-state/page-navigation.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { ExtractedLocationStoreService } from '../../../service/overlay/extracted-location-store.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { associate, filterFormArray, formatMoney } from '@ps-gui/shared-utils';
import { TableFormArrayCache } from '../../../service/table-form-array-cache.service';
import { CurrentTableCell } from '../../../models/interfaces';
import { ExtractedRowForm, FieldFormControl } from '../../../models/extraction-form-models';
import {
  ExtractedTableCell,
  ExtractedValuesByKey,
  isTableReference,
  RowMembership,
} from '../../../models/extracted-values';
import {
  FieldModel,
  getTableModelByColumnKey,
} from '../../../models/extraction-models/extraction-models';
import { calculateSumOfCurrencyColumnInCents } from '../../../utils/Table.utils';
import { Kopfdaten } from '../../../models/extraction-models/proof/kopfdaten-extraction-model';
import { ExtractionField } from '../../../pages/extraction-page/extraction-page.component';
import { NgClass } from '@angular/common';
import { TooltipDirective } from '@vydaps/gui-commons';
import { TableView } from '../table-view';
import { CurrencyInputDirective } from '../../../directives/currency-input.directive';
import { OcrHighlightService } from '../../../service/overlay/ocr-highlight.service';

import {
  DefaultInputStylingService,
  INPUT_STYLING_SERVICE,
} from '../../../service/input-styling.service';
import { parseValue } from '../../../utils/type-parsers';
import { DevToolsCardService, errorText } from '@ps-gui/ui';
import { CtBatchStoreService } from '../../../service/app-state/ct-batch-store.service';
import { WarningComponent } from '../warnings/warning.component';
import { RemoveAllRowsDialogComponent } from '../../dialogs/remove-all-rows-dialog/remove-all-rows-dialog.component';
import { TableData } from '../../../table-overlay/table-overlay';
import { sortBy } from '@vydaps/gui-commons/arrays';
import { parseCurrency } from '../../../utils/Parser.utils';
import { generateTable, TableRequest } from '../../../dev-tools/generators/table.generator';
import { TableAction, TablePanelComponent } from '../../../dev-tools/panels/table-panel.component';

@Component({
  selector: 'ct-extraction-table-extraction-view',
  templateUrl: './table-extraction-view.component.html',
  host: { class: 'flex min-h-0 flex-col' },
  providers: [
    {
      provide: INPUT_STYLING_SERVICE,
      useClass: DefaultInputStylingService,
    },
  ],
  imports: [
    TableComponent,
    FormsModule,
    InputNumberModule,
    ConfirmDialogModule,
    ReactiveFormsModule,
    NgClass,
    TooltipDirective,
    CurrencyInputDirective,
    WarningComponent,
    RemoveAllRowsDialogComponent,
  ],
})
export class TableExtractionViewComponent extends TableView implements OnInit {
  private readonly pageNavigationService = inject(PageNavigationService);
  private readonly extractedTableRemovalService = inject(ExtractedTableRemovalService);
  private readonly extractedTableCreationService = inject(ExtractedTableCreationService);
  private readonly toolbarService = inject(ToolbarService);
  protected readonly inputStylingService = inject(INPUT_STYLING_SERVICE);
  protected readonly batchStoreService = inject(CtBatchStoreService);
  private readonly devToolsCardService = inject(DevToolsCardService);
  private readonly confirmationService = inject(ConfirmationService);

  private readonly extractedLocationStoreService = inject(ExtractedLocationStoreService);
  private readonly highlightService = inject(OcrHighlightService);
  protected readonly tableFormArrayCache = inject(TableFormArrayCache);

  protected readonly destroyRef = inject(DestroyRef);

  readonly currentFieldModel = input<FieldModel>();
  readonly displayHeader = input(true);
  readonly hasMultipleTableSections = input(false);

  /**
   * Controls whether table creation should be enabled by default. Even with this flag enabled,
   * table creation will only be activated if there are no tables or unconfirmed tables exist,
   * see the respective effect below.
   */
  readonly enableTableCreationPerDefault = input(true);

  readonly initialFieldData = input.required<Partial<ExtractedValuesByKey>>();

  protected currentTableCell?: CurrentTableCell = undefined;

  protected suggestedSumModel = Kopfdaten.kopfdaten_gesamtbetrag;
  protected suggestedSumControl: FieldFormControl | undefined;

  // Several tables can be on screen at once, so each confirmation needs a key of its own.
  protected readonly removeAllRowsDialogKey = computed(
    () => `removeAllRowsDialog-${this.formArrayCacheKey()}`,
  );

  /**
   * In theory, this output can be used to signal that the user wants
   * to continue to the next section after this table (which the parent component
   * uses to activate the next section or continue to the next page).
   * Currently, this is not possible since pressing "Enter" inside the last table cell
   * creates a new table row in the same table (by design).
   * I could think of a keybind like `Shift+Enter` to continue in the feature.
   */
  readonly continue = output();

  readonly fieldValueChanged = output<ExtractedValuesByKey>();

  readonly currentExtractionFieldChange = output<ExtractionField | undefined>();
  readonly currentTableFieldIndex = signal<number | undefined>(0);

  readonly isActiveSection = input.required<boolean>();

  constructor() {
    super();

    effect(() => {
      if (!this.enableTableCreationPerDefault()) return;

      const tablesOnDocument = (this.document()?.pages ?? [])
        .map((page) => page.id)
        .flatMap((id) => this.tableOverlayStoreService.getTablesOnPage(id));

      this.toolbarService.setTableCreationEnabled(
        tablesOnDocument.length === 0 ||
          !tablesOnDocument.every((tableData) => tableData.confirmed()),
      );
    });

    effect(() => {
      const tableFieldIndex = this.currentTableFieldIndex();
      if (tableFieldIndex !== undefined) {
        this.currentExtractionFieldChange.emit(undefined);
      }
    });
  }

  // Runs after createSuggestedSumControl, so the panel already knows whether there is a sum.
  private registerDevToolsProvider(): void {
    const id = `table-${this.formArrayCacheKey()}`;

    this.devToolsCardService.register({
      id,
      label: this.currentTableModel().label,
      panel: TablePanelComponent,
      inputs: { hasSum: !!this.suggestedSumControl },
      apply: (action: TableAction) => this.applyTableAction(action),
    });

    this.destroyRef.onDestroy(() => this.devToolsCardService.unregister(id));
  }

  private applyTableAction(action: TableAction): void {
    if (action.type === 'removeAll') {
      this.openRemoveAllRowsDialog();
      return;
    }

    this.applyGeneratedRows(action.request);
  }

  private applyGeneratedRows(request: TableRequest): void {
    const formArray = this.tableRows();

    // An empty row left over from "Manuell erstellen" would push the generated rows down.
    const emptyRows = formArray.controls.filter((rowForm) => !this.hasAnyValue(rowForm));
    this.removeFieldLocationData(emptyRows);
    filterFormArray(formArray, (rowForm) => !emptyRows.includes(rowForm));

    const { rows, sum } = generateTable({
      ...request,
      fieldModels: this.currentTableModel().fieldModels,
      existingRows: formArray.getRawValue(),
    });

    // Pushing hundreds of rows one by one would run validation and persistence per row.
    rows.forEach((row) =>
      formArray.push(
        this.createRowForm({ ...row, rowMembership: this.getDefaultRowMembership() }),
        { emitEvent: false },
      ),
    );

    if (sum !== null) {
      this.suggestedSumControl?.setValue(sum, { emitEvent: false });
    }

    formArray.updateValueAndValidity();
    this.suggestedSumControl?.updateValueAndValidity();
  }

  onTableCellChange(cell: CurrentTableCell) {
    this.currentTableCell = cell;
    this.currentExtractionFieldChange.emit({
      model: getTableModelByColumnKey(
        this.batchStoreService.ctBatchDomain,
        cell.fieldKey,
      )!.fieldModels.find((model) => model.key === cell.fieldKey)!,
      idOverride: cell.cellForm.controls.id.value,
      control: cell.cellForm.controls.value,
    });
  }

  ngOnInit() {
    this.createSuggestedSumControl();
    this.registerDevToolsProvider();

    this.pageNavigationService
      .navigate$('continue')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => filterFormArray(this.tableRows(), (rowForm) => this.hasAnyValue(rowForm)));

    this.extractedTableCreationService.createdTable$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((createdTable) => {
        this.handleNewCreatedTable(createdTable);
      });

    this.extractedTableRemovalService.destroyedTable$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ tableId }) => this.removeMatchingTableRows(tableId));

    this.suggestedSumControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.fieldValueChanged.emit({
          [this.suggestedSumModel.key]: value ? parseCurrency(String(value)) : null,
        });
      });
  }

  private handleNewCreatedTable(createdTable: CreatedTable) {
    const { rows, suggestedFieldLocations } = createdTable;
    if (rows.length <= 0) {
      return;
    }

    const pageId = createdTable.tableData.pageId;

    const documentId = this.documentId();
    if (documentId) {
      Object.entries(suggestedFieldLocations).forEach(([fieldId, fieldLocation]) => {
        this.extractedLocationStoreService.replaceExtractedLocation(documentId, pageId, fieldId, [
          fieldLocation,
        ]);
      });
    }

    const tables = this.tableOverlayStoreService.getTablesOnPage(pageId);

    const tableY = (table: TableData) => table.lines.horizontal()[0].offset();
    const tableBelow = sortBy(tables, tableY).find(
      (table) => tableY(table) > tableY(createdTable.tableData),
    );

    let insertionIndex = tableBelow
      ? this.tableRows().controls.findIndex((row) => {
          const membership = row.controls.rowMembership.value;
          return isTableReference(membership) && membership.tableId === tableBelow.id;
        })
      : this.tableRows().length;

    rows
      .map((extractedRow) => this.createRowForm(extractedRow))
      .forEach((row) => {
        this.tableRows().insert(insertionIndex, row, { emitEvent: false });
        insertionIndex++;
      });

    this.tableRows().markAllAsTouched();
    this.tableRows().updateValueAndValidity();
  }

  private createSuggestedSumControl() {
    if (this.currentTableModel().key !== 'forderungen') return;

    const value = this.initialFieldData()[this.suggestedSumModel.key];

    const control = this.formBuilder.control(
      value ? parseValue(String(value), this.suggestedSumModel).value : null,
    );

    control.addValidators(this.suggestedSumModel.dataAwareValidator(control, this.tableRows));
    this.suggestedSumControl = control;
  }

  protected override onTableRowsUpdated() {
    super.onTableRowsUpdated();
    if (this.suggestedSumControl) {
      this.suggestedSumControl.updateValueAndValidity();
      this.updateSuggestedSumDisplayValue();
    }
  }

  startManualCreation() {
    this.toolbarService.setTableCreationEnabled(false);
    this.tableRows().push(
      this.createRowForm({
        rowData: this.createEmptyRowData(),
        rowMembership: this.getDefaultRowMembership(),
      }),
    );
  }

  removeMatchingTableRows(tableId: string): void {
    this.removeFieldLocationData(
      this.tableRows().controls.filter((rowForm) => {
        const rowMembership = rowForm.controls.rowMembership.value;
        return isTableReference(rowMembership) && rowMembership.tableId === tableId;
      }),
    );

    filterFormArray(this.tableRows(), (rowForm) => {
      const membership = rowForm.getRawValue().rowMembership;
      return !isTableReference(membership) || membership.tableId !== tableId;
    });
  }

  private removeFieldLocationData(rows: ExtractedRowForm[]) {
    const documentId = this.documentId();
    if (!documentId) return;

    rows.forEach((row) => {
      Object.values(row.controls.rowData.controls).forEach((fieldControl) => {
        this.extractedLocationStoreService.deleteExtractedLocation(
          documentId,
          fieldControl.controls.id.value,
        );
      });
    });

    this.highlightService.clearHighlight();
  }

  private getDefaultRowMembership(): RowMembership {
    const documentId = this.documentId();
    return documentId ? { documentId } : undefined;
  }

  addRowAtIndex(index: number, tableId: string | undefined): void {
    const shouldAddedRowReferenceTable =
      tableId !== undefined && this.isRowAtIndexInTable(index - 1, tableId);
    const rowFormGroup: ExtractedRowForm = this.createRowForm({
      rowData: this.createEmptyRowData(),
      rowMembership: shouldAddedRowReferenceTable ? { tableId } : this.getDefaultRowMembership(),
    });

    this.tableRows()?.insert(index, rowFormGroup);

    rowFormGroup.markAsPristine();
    rowFormGroup.markAsUntouched();
    rowFormGroup.updateValueAndValidity({ emitEvent: false });
  }

  private isRowAtIndexInTable(rowIndex: number, tableId: string): boolean {
    const previousRowMembership: RowMembership | undefined =
      this.tableRows()?.controls[rowIndex]?.getRawValue().rowMembership;
    return (
      previousRowMembership !== undefined &&
      isTableReference(previousRowMembership) &&
      previousRowMembership.tableId === tableId
    );
  }

  removeRowAtIndex(index: number): void {
    this.removeFieldLocationData([this.tableRows().controls[index]]);
    this.tableRows().removeAt(index);
  }

  private openRemoveAllRowsDialog(): void {
    this.confirmationService.confirm({ key: this.removeAllRowsDialogKey() });
  }

  protected removeAllRows(): void {
    this.removeFieldLocationData([...this.tableRows().controls]);
    this.tableRows().clear();
    this.suggestedSumControl?.setValue(null);
  }

  private hasAnyValue(rowFormGroup: ExtractedRowForm) {
    return Object.values(rowFormGroup.controls.rowData.controls).some(
      (cellControl) => cellControl.value.value !== null && cellControl.value.value !== '',
    );
  }

  protected readonly suggestedSumDisplayValue = signal('');

  private updateSuggestedSumDisplayValue() {
    const sumInCents = calculateSumOfCurrencyColumnInCents(
      this.tableRows().getRawValue(),
      'forderungen_betrag',
    );
    this.suggestedSumDisplayValue.set(formatMoney(sumInCents / 100) ?? '');
  }

  private createEmptyRowData(): Record<string, ExtractedTableCell> {
    const fieldModels = this.currentTableModel().fieldModels;
    return associate(
      fieldModels,
      (model) => model.key,
      (_) => {
        return {
          value: null,
          id: self.crypto.randomUUID(),
        };
      },
    );
  }

  onSuggestedSumControlFocus() {
    this.currentTableCell = undefined;
    this.currentTableFieldIndex.set(undefined);
    this.currentExtractionFieldChange.emit({
      model: this.suggestedSumModel,
      control: this.suggestedSumControl,
    });
  }

  protected readonly errorText = errorText;
}
